import { useDispatch, useSelector } from 'react-redux';
import { getSignupDataValue, getSignupDataGtkmData } from '../store/signup-data/selectors';
import { Data } from 'signup-constants/signupData/Data';
import { retryEndpoint } from '../store/http-responses/action-creators';
import { EndpointKey } from '../http/EndpointKey';
import { getEndpointRetries } from '../store/http-responses/selectors';
import { updateStepOrdersInDomainStep } from '../http-gtkm-company-info/updateStepOrders';
import { setGtkmAnimationStart, setGtkmShowingErrorModal, setGtkmAnimationShowingCount, setGtkmHasDomainChangedInDomainStep } from '../store/gtkm/action-creator';
import { getGtkmAnimationShowingCount } from '../store/gtkm/selector';
import { useView } from '../views/useView';
import { Step } from 'signup-constants/Step';
import { getGTKMEndpointKey } from '../utils/gtkm/getGTKMEndpointKey';
import { updateFlowForGTKM } from '../http-gtkm-company-info/handleACQ175GTKMCompanyInfoResponse';
export const hasGTKMDomainChanged = state => {
  const companyDomain = getSignupDataValue(state, Data.CompanyDomain);
  let gtkmCompanyDomain = getSignupDataGtkmData(state, Data.CompanyDomain);
  if (gtkmCompanyDomain === 'hubspot.com') {
    gtkmCompanyDomain = 'hubspot-test.com';
  }
  return gtkmCompanyDomain !== companyDomain;
};
export const useHasDomainChanged = () => {
  const appState = useSelector(state => state);
  return hasGTKMDomainChanged(appState);
};
export const useGTKMDomainSubmit = () => {
  const dispatch = useDispatch();
  const hasDomainChanged = useHasDomainChanged();
  const gtkmDomain = useSelector(state => getSignupDataGtkmData(state, Data.CompanyDomain));
  const animationShowingCount = useSelector(state => getGtkmAnimationShowingCount(state));
  const {
    submit
  } = useView({
    stepKey: Step.CompanyDomain
  });
  const appState = useSelector(state => state);
  const endpointKey = getGTKMEndpointKey(appState);
  const gtkmEndpointRetries = useSelector(state => getEndpointRetries(state, endpointKey));
  const haveNotInvokedSecondGTKMAPI = gtkmEndpointRetries < 1;
  const handleSubmit = () => {
    dispatch(setGtkmShowingErrorModal(false));
    dispatch(setGtkmHasDomainChangedInDomainStep(hasDomainChanged));
    // In the following cases, we don't want to show the animation and hope just to go to next step directly
    // 1. The second GTKM API have been invoked
    // 2. The domain hasn't changed but already shown the animation once
    if (!haveNotInvokedSecondGTKMAPI || !hasDomainChanged && animationShowingCount >= 1) {
      submit();

      // When the domain has changed and we haven't invoked the second GTKM API,
      // we want to show the animation and retry the second GTKM API
    } else if (hasDomainChanged && haveNotInvokedSecondGTKMAPI) {
      dispatch(setGtkmAnimationStart());
      dispatch(setGtkmAnimationShowingCount(animationShowingCount + 1));
      dispatch(retryEndpoint(endpointKey));

      // When the domain has not been changed and we haven't invoked the second GTKM API,
      // we want to show the animation and update the step orders
    } else {
      dispatch(setGtkmAnimationStart());
      dispatch(setGtkmAnimationShowingCount(animationShowingCount + 1));
      updateStepOrdersInDomainStep({
        gtkmData: {
          [Data.CompanyDomain]: gtkmDomain
        },
        dispatch,
        state: appState
      });
    }
  };
  const handleSubmitACQ175 = () => {
    dispatch(setGtkmShowingErrorModal(false));
    dispatch(setGtkmHasDomainChangedInDomainStep(hasDomainChanged));
    const hasInvokedSecondGTKMAPI = gtkmEndpointRetries >= 1;
    const hasShownAnimation = animationShowingCount >= 1;

    // Use default submit when either:
    // 1. The GTKM API has been invoked twice
    // 2. The domain hasn't changed and the animation has been already been shown
    const shouldUseDefaultSubmit = hasInvokedSecondGTKMAPI || !hasDomainChanged && hasShownAnimation;
    const shouldRetry = hasDomainChanged && !hasInvokedSecondGTKMAPI;
    const startAnimation = () => {
      dispatch(setGtkmAnimationStart());
      dispatch(setGtkmAnimationShowingCount(animationShowingCount + 1));
    };
    if (shouldUseDefaultSubmit) {
      submit();
      return;
    }
    if (shouldRetry) {
      startAnimation();
      dispatch(retryEndpoint(EndpointKey.GTKMCompanyInfoACQ175));
      return;
    }

    // If the domain has not changed and we haven't invoked the GTKM API for a second time,
    // we want to show the animation and remove the review step so the user can correct data as
    // an API call won't be made
    startAnimation();
    updateFlowForGTKM(appState, dispatch, [Step.Review]);
  };
  return {
    handleSubmit,
    handleSubmitACQ175
  };
};